/**
 * Notifications UI for offline queue and sync status.
 *
 * Renders a compact icon that opens a popover listing pending changes
 * (create/update/delete). Items can be marked read, reverted (Undo), or
 * retried if they failed. When the app reconnects, items are marked
 * "synced" only after the backend confirms the write.
 */
import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Badge } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { useNotifications } from './NotificationsProvider.jsx';
import { getQueue, removeItem, updateItem } from '../Utils/offlineQueue';

/**
 * @param {{ top:number, right:number, left?:number, bottom?:number }} position - absolute position when not inline
 * @param {boolean} showWhenOnline - if false, the icon hides online unless there are notifications
 * @param {boolean} inline - render inside header flow instead of fixed positioning
 */
export default function NotificationBell({ position = { top: 10, right: 12 }, showWhenOnline = true, inline = false }) {
  const { items, remove, markRead, markAllRead, clearRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [hovered, setHovered] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (!open) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (open && e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const unreadCount = items.filter((n) => !n.read).length;
  // Hide online only when there are no notifications
  if (!showWhenOnline && isOnline && items.length === 0) return null;

  console.log('[NotificationBell] unreadCount', unreadCount);

  const containerStyle = inline
    ? { position: 'relative', zIndex: 10000, WebkitAppRegion: 'no-drag', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }
    : { position: 'fixed', zIndex: 10000, WebkitAppRegion: 'no-drag', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, ...position };

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      className="clickable"
      data-clickable="true"
    >
      <IconButton
        aria-label="notifications"
        onClick={() => { console.log('[NotificationBell] clicked'); setOpen(!open); }}
        title={`${unreadCount} notifications`}
        size="large"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ WebkitAppRegion: 'no-drag', pointerEvents: 'auto', color: hovered ? '#3498B7' : '#fff', transition: 'color 120ms ease-in-out' }}
      >
        <Badge badgeContent={unreadCount} color="error" overlap="circular">
          <SyncIcon />
        </Badge>
      </IconButton>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 44, width: 420, maxHeight: 420,
          overflowY: 'auto', background: '#2E2E2E', color: '#fff', borderRadius: 8,
          border: '1px solid rgb(111, 111, 111)', padding: 8, boxShadow: '0 6px 12px rgba(0,0,0,0.25)', WebkitAppRegion: 'no-drag', pointerEvents: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontWeight: 600, fontSize: 12 }}>Sync status</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => {
                try {
                  // Remove only notifications marked as synced
                  (items || []).forEach((n) => {
                    if (n.queueItem?.status === 'synced') {
                      remove(n.id);
                    }
                  });
                } catch (e) {
                  console.warn('Clear synced failed:', e);
                }
              }} style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>Clear synced</button>
            </div>
          </div>
          {items.length === 0 && <div style={{ padding: 8, opacity: 0.8 }}>No events changed since last sync</div>}
          {items.map((n) => (
            <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)', background: n.queueItem?.status === 'synced' ? 'rgba(46, 204, 113, 0.08)' : 'transparent' }}>
              <div style={{ fontSize: 12, flex: 1, paddingRight: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600 }}>{headerTitle(n)}</div>
                </div>
                <div style={{ opacity: 0.9, fontSize: 11, marginTop: 2 }}>
                  {renderDiff(n)}
                </div>
              </div>
              
              
              <div style={{ display: 'flex', flexDirection:"column", alignItems: 'center', gap: 6 }}>
              {n.queueItem?.status && (
                    <span style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: statusBg(n.queueItem.status),
                      color: '#fff',
                      opacity: 0.95,
                      marginLeft: 'auto'
                    }}>
                      {n.queueItem.status}
                    </span>
                  )}
                  {n.queueItem?.supersededBy && (
                    <span style={{ fontSize: 10, opacity: 0.7 }}>superseded</span>
                  )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {!n.read && (
                  <button onClick={() => markRead(n.id)} style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>Read</button>
                )}
                {n.queueItem && (
                  <button
                    onClick={async () => {
                      try {
                        const qi = n.queueItem;
                        if (!qi?.type || !qi?.collection) return;
                        const { collection, query, where, getDocs, doc, addDoc, updateDoc, deleteDoc } = await import('firebase/firestore');
                        const { database } = await import('../Utils/firebaseConfig');
                        if (qi.type === 'create') {
                          const q = query(collection(database, qi.collection), where('id', '==', qi.id));
                          const snap = await getDocs(q);
                          if (!snap.empty) {
                            const targetRef = doc(database, qi.collection, snap.docs[0].id);
                            await deleteDoc(targetRef);
                          }
                          removeItem(qi.key);
                        } else if (qi.type === 'update') {
                          if (!qi.before) return;
                          const q = query(collection(database, qi.collection), where('id', '==', qi.id));
                          const snap = await getDocs(q);
                          if (!snap.empty) {
                            const targetRef = doc(database, qi.collection, snap.docs[0].id);
                            await updateDoc(targetRef, { ...qi.before });
                          }
                          removeItem(qi.key);
                        } else if (qi.type === 'delete') {
                          if (!qi.before) return;
                          await addDoc(collection(database, qi.collection), { ...qi.before });
                          removeItem(qi.key);
                        }
                        markRead(n.id);
                        remove(n.id);
                      } catch (err) {
                        console.error('Revert failed:', err);
                      }
                    }}
                    disabled={Boolean(n.queueItem?.supersededBy)}
                    style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6, padding: '2px 6px', cursor: n.queueItem?.supersededBy ? 'not-allowed' : 'pointer', opacity: n.queueItem?.supersededBy ? 0.5 : 1, fontSize: 11 }}
                  >
                    Undo
                  </button>
                )}
                {n.queueItem && n.queueItem.status === 'failed' && (
                  <button
                    onClick={async () => {
                      try {
                        const qi = n.queueItem;
                        const { collection, query, where, getDocs, doc, updateDoc, addDoc } = await import('firebase/firestore');
                        const { database } = await import('../Utils/firebaseConfig');
                        if (qi.type === 'update') {
                          const q = query(collection(database, qi.collection), where('id', '==', qi.id));
                          const snap = await getDocs(q);
                          if (!snap.empty) {
                            const targetRef = doc(database, qi.collection, snap.docs[0].id);
                            await updateDoc(targetRef, { ...(qi.after || {}) });
                          }
                        } else if (qi.type === 'create') {
                          await addDoc(collection(database, qi.collection), { ...(qi.after || {}) });
                        }
                        markRead(n.id);
                        remove(n.id);
                      } catch (err) {
                        console.error('Retry failed:', err);
                      }
                    }}
                    disabled={Boolean(n.queueItem?.supersededBy)}
                    style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6, padding: '2px 6px', cursor: n.queueItem?.supersededBy ? 'not-allowed' : 'pointer', opacity: n.queueItem?.supersededBy ? 0.5 : 1, fontSize: 11 }}
                  >
                    Retry
                  </button>
                )}
              </div>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Get a compact header string for a notification (e.g., "Title | Updated"). */
function headerTitle(n) {
  const qi = n.queueItem;
  const title = qi?.after?.title || qi?.before?.title || n.title || n.type || 'Notification';
  const type = !qi ? '' : qi.type === 'update' ? 'Updated' : qi.type === 'delete' ? 'Deleted' : qi.type === 'create' ? 'Created' : '';
  return type ? `${title} | ${type}` : title;
}

/** Map a status to a subtle background color. */
function statusBg(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'queued') return 'transparent';
  if (s === 'synced') return 'rgba(46, 204, 113, 0.35)';
  if (s === 'failed') return 'rgba(231, 76, 60, 0.35)';
  if (s === 'conflict') return 'rgba(155, 89, 182, 0.35)';
  return 'rgba(255,255,255,0.12)';
}

/**
 * Render a compact field diff for a notification entry.
 * - Dates are formatted as DD.MM.YY HH:mm
 * - Resource ids are resolved to names when available
 */
function renderDiff(n) {
  const qi = n.queueItem;
  if (!qi) return <span>{n.message || n.entity}</span>;
  if (qi.type === 'create') {
    return <span>Created “{qi.after?.title || qi.title}” {dateSpan(qi.after)}</span>;
  }
  if (qi.type === 'delete') {
    return <span>Deleted “{qi.before?.title || qi.title}” {dateSpan(qi.before)}</span>;
  }
  const before = qi.before || {};
  const after = qi.after || {};
  const fields = ['title','start','end','allDay','status','color','resource'];
  const diffs = fields
    .filter((f) => normalize(before[f]) !== normalize(after[f]))
    .map((f) => (
      <div key={f} style={{ display: 'flex', gap: 6, lineHeight: 1.25 }}>
        <span style={{ opacity: 0.7, minWidth: 58 }}>{f}:</span>
        <span>{f === 'resource' ? formatResources(before[f]) : formatVal(before[f])}</span>
        <span>→</span>
        <span>{f === 'resource' ? formatResources(after[f]) : formatVal(after[f])}</span>
      </div>
    ));
  return diffs.length ? diffs : <span>Updated “{after.title || before.title || qi.title}”</span>;
}

/** Format scalars, arrays and Firestore timestamps for display. */
function formatVal(v) {
  if (v == null) return '—';
  if (v instanceof Date) return formatDate(v);
  if (typeof v === 'string') {
    const parsed = new Date(v);
    if (!isNaN(parsed.getTime())) return formatDate(parsed);
    return v;
  }
  if (Array.isArray(v)) return `[${v.join(', ')}]`;
  if (v && v.toDate instanceof Function) return formatDate(v.toDate());
  if (typeof v === 'object' && v.seconds != null) {
    try { return formatDate(new Date(v.seconds * 1000)); } catch (_) {}
  }
  return String(v);
}

/** Normalize values into comparable strings for diffing. */
function normalize(v) {
  if (v == null) return '';
  if (v instanceof Date) return v.toISOString();
  if (v && v.toDate instanceof Function) return v.toISOString ? v.toISOString() : v.toDate().toISOString();
  if (Array.isArray(v)) return JSON.stringify(v);
  return String(v);
}

/** Format (start → end) span, if both are present. */
function dateSpan(obj) {
  if (!obj) return '';
  const s = obj.start ? formatVal(obj.start) : '';
  const e = obj.end ? formatVal(obj.end) : '';
  return s && e ? `(${s} → ${e})` : '';
}

/** Format a Date as DD.MM.YY HH:mm. */
function formatDate(d) {
  try {
    const date = d instanceof Date ? d : new Date(d);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yy} ${hh}:${min}`;
  } catch (_) {
    return String(d);
  }
}

/** Translate resource id(s) to comma-separated names using the live id->name cache. */
function formatResources(value) {
  try {
    if (!value) return '—';
    const arr = Array.isArray(value) ? value : [value];
    const map = getResourceNameMap();
    const names = arr.map((id) => map.get(Number(id)) || map.get(String(id)) || id);
    return names.join(', ');
  } catch (_) {
    return String(value);
  }
}

// Build a flat id->name map from the nested resources tree
/** Build/read a stable id->name map from the Firestore-backed cache. */
let __resourceNameMapCache = null;
function getResourceNameMap() {
  if (__resourceNameMapCache) return __resourceNameMapCache;
  const map = new Map();
  try {
    const globalMap = (typeof window !== 'undefined' && window.__resourceNameMap) || {};
    Object.keys(globalMap).forEach((k) => map.set(isNaN(Number(k)) ? k : Number(k), globalMap[k]));
  } catch (_) {}
  __resourceNameMapCache = map;
  return map;
}


